import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { DollarSign, Users, TrendingUp, FileText, Shield, LogOut, ScrollText, AlertTriangle, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { OverviewTab } from './admin/OverviewTab';
import { TransactionsTab } from './admin/TransactionsTab';
import { StaffTab } from './admin/StaffTab';
import { TaxTab } from './admin/TaxTab';
import { AuditTab } from './admin/AuditTab';
import { OrdersTab } from './admin/OrdersTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Small helper: swallow expected auth-refresh 401s (axios interceptor
// already handles login redirect) but surface real errors (5xx, network,
// bugs) so they don't silently disappear from prod monitoring.
const logIfUnexpected = (err, tag) => {
  const status = err?.response?.status;
  if (status !== 401 && status !== 403) {
    console.error(`[AdminDashboard/${tag}]`, status || err?.message || err);
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [donations, setDonations] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [staffForm, setStaffForm] = useState({ name: '', email: '', role: '', revenue_share_percent: 0, paypal_email: '' });
  const [staff, setStaff] = useState([]);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [taxReport, setTaxReport] = useState(null);
  const [complianceReport, setComplianceReport] = useState(null);
  // PayPal config
  const [ppConfig, setPpConfig] = useState(null);
  const [ppForm, setPpForm] = useState({ mode: 'live', client_id: '', client_secret: '' });
  const [ppShowSecret, setPpShowSecret] = useState(false);
  const [ppSaving, setPpSaving] = useState(false);
  // Integrations (Resend + Slack)
  const [intConfig, setIntConfig] = useState(null);
  const [intForm, setIntForm] = useState({ resend_api_key: '', sender_email: '', slack_webhook_url: '', admin_email: '' });
  const [intShowKey, setIntShowKey] = useState(false);
  const [intSaving, setIntSaving] = useState(false);
  // Password rotation
  const [rotating, setRotating] = useState(false);
  const [rotationResult, setRotationResult] = useState(null);
  // Audit Trail
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditActions, setAuditActions] = useState([]);
  const [auditFilter, setAuditFilter] = useState({ action: '', actor: '', status: '' });
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  // Advanced staff payments
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingTotalOwed, setPendingTotalOwed] = useState(0);
  const [staffSummary, setStaffSummary] = useState([]);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [downloadingPacket, setDownloadingPacket] = useState(false);
  // Revenue chart
  const [revenueSeries, setRevenueSeries] = useState([]);
  const [revenueRange, setRevenueRange] = useState(30);
  // Audit CSV export
  const [csvRange, setCsvRange] = useState({ start_date: '', end_date: '' });
  const [exportingCsv, setExportingCsv] = useState(false);
  // Boot secret-source health (env / mongo / ephemeral)
  const [secretStatus, setSecretStatus] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [statsRes, donationsRes, staffRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/donations`),
        axios.get(`${API}/admin/staff`)
      ]);
      setStats(statsRes.data);
      setDonations(donationsRes.data);
      setStaff(staffRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin');
        return;
      }
      toast.error('Failed to load dashboard data');
    }
  }, [navigate]);

  const fetchPaypalConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/paypal-config`);
      setPpConfig(res.data);
      setPpForm(f => ({ ...f, mode: res.data.mode || 'live' }));
    } catch (e) { logIfUnexpected(e, 'fetchPaypalConfig'); }
  }, []);

  const fetchIntegrationsConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/integrations-config`);
      setIntConfig(res.data);
    } catch (e) { logIfUnexpected(e, 'fetchIntegrationsConfig'); }
  }, []);

  const fetchSecretStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/secret-status`);
      setSecretStatus(res.data);
    } catch (err) {
      logIfUnexpected(err, 'secret-status');
    }
  }, []);

  useEffect(() => {
    // Auth check is implicit via cookie. The axios interceptor will refresh or 401 us.
    fetchDashboardData();
    fetchPaypalConfig();
    fetchIntegrationsConfig();
    fetchSecretStatus();
  }, [fetchDashboardData, fetchPaypalConfig, fetchIntegrationsConfig, fetchSecretStatus]);

  const fetchAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (auditFilter.action) params.set('action', auditFilter.action);
      if (auditFilter.actor) params.set('actor', auditFilter.actor);
      if (auditFilter.status) params.set('status', auditFilter.status);
      const [logsRes, actionsRes] = await Promise.all([
        axios.get(`${API}/admin/audit/logs?${params.toString()}`),
        axios.get(`${API}/admin/audit/actions`),
      ]);
      setAuditLogs(logsRes.data.logs || []);
      setAuditTotal(logsRes.data.total || 0);
      setAuditActions(actionsRes.data.actions || []);
    } catch (e) {
      toast.error('Failed to load audit logs');
    } finally {
      setAuditLoading(false);
    }
  }, [auditFilter]);

  const fetchPendingPayments = useCallback(async () => {
    try {
      const [pendRes, sumRes] = await Promise.all([
        axios.get(`${API}/accounting/staff-payments/pending`),
        axios.get(`${API}/accounting/staff-payments/summary?year=${new Date().getFullYear()}`),
      ]);
      setPendingPayments(pendRes.data.payments || []);
      setPendingTotalOwed(pendRes.data.total_owed || 0);
      setStaffSummary(sumRes.data.staff_summary || []);
    } catch (e) { logIfUnexpected(e, 'fetchPendingPayments'); }
  }, []);

  const fetchRevenueSeries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/revenue-daily?days=${revenueRange}`);
      setRevenueSeries(res.data.series || []);
    } catch (e) { logIfUnexpected(e, 'fetchRevenueSeries'); }
  }, [revenueRange]);

  useEffect(() => {
    if (activeTab === 'audit') fetchAuditLogs();
    if (activeTab === 'staff') fetchPendingPayments();
    if (activeTab === 'overview') fetchRevenueSeries();
  }, [activeTab, fetchAuditLogs, fetchPendingPayments, fetchRevenueSeries]);

  const markStaffPaymentPaid = async (paymentId) => {
    const txId = window.prompt('PayPal transaction ID (optional):') || '';
    setMarkingPaidId(paymentId);
    try {
      await axios.post(`${API}/accounting/staff-payments/mark-paid`, {
        payment_id: paymentId,
        paypal_transaction_id: txId || null,
      });
      toast.success('Staff payment marked as paid — recorded to audit trail.');
      fetchPendingPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark paid');
    } finally {
      setMarkingPaidId(null);
    }
  };

  const downloadTaxPacket = async () => {
    setDownloadingPacket(true);
    try {
      const res = await axios.get(`${API}/accounting/tax-packet.zip?year=${taxYear}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `tax_packet_${taxYear}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded tax_packet_${taxYear}.zip`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download tax packet');
    } finally {
      setDownloadingPacket(false);
    }
  };

  const exportAuditCsv = async () => {
    setExportingCsv(true);
    try {
      const params = new URLSearchParams();
      if (auditFilter.action) params.set('action', auditFilter.action);
      if (auditFilter.actor) params.set('actor', auditFilter.actor);
      if (auditFilter.status) params.set('status', auditFilter.status);
      if (csvRange.start_date) params.set('start_date', csvRange.start_date);
      if (csvRange.end_date) params.set('end_date', csvRange.end_date);
      const res = await axios.get(`${API}/admin/audit/logs.csv?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `audit_logs_${ts}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Audit log exported to CSV');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to export CSV');
    } finally {
      setExportingCsv(false);
    }
  };

  const saveIntegrations = async (e) => {
    e.preventDefault();
    setIntSaving(true);
    try {
      const body = {};
      if (intForm.resend_api_key.trim()) body.resend_api_key = intForm.resend_api_key.trim();
      if (intForm.sender_email.trim()) body.sender_email = intForm.sender_email.trim();
      if (intForm.slack_webhook_url.trim()) body.slack_webhook_url = intForm.slack_webhook_url.trim();
      if (intForm.admin_email.trim()) body.admin_email = intForm.admin_email.trim();
      if (Object.keys(body).length === 0) {
        toast.error('Nothing to save — fill at least one field');
        return;
      }
      const res = await axios.post(`${API}/admin/integrations-config`, body);
      setIntConfig(res.data);
      setIntForm({ resend_api_key: '', sender_email: '', slack_webhook_url: '', admin_email: '' });
      toast.success('Integrations updated — new values active immediately.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save integrations');
    } finally {
      setIntSaving(false);
    }
  };

  const rotateAdminPassword = async () => {
    if (!window.confirm('This will immediately change your admin password. You will need the new password to log in again. Continue?')) return;
    setRotating(true);
    setRotationResult(null);
    try {
      const res = await axios.post(`${API}/admin/rotate-password`, {});
      setRotationResult(res.data);
      if (res.data.emailed) {
        toast.success(`New password generated and emailed to ${res.data.email_recipient}`);
      } else {
        toast.warning('New password generated (email delivery failed — save it now!)');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rotate password');
    } finally {
      setRotating(false);
    }
  };

  const savePaypalConfig = async (e) => {
    e.preventDefault();
    setPpSaving(true);
    try {
      const body = { mode: ppForm.mode };
      if (ppForm.client_id.trim()) body.client_id = ppForm.client_id.trim();
      if (ppForm.client_secret.trim()) body.client_secret = ppForm.client_secret.trim();
      const res = await axios.post(`${API}/admin/paypal-config`, body);
      setPpConfig(res.data);
      setPpForm(f => ({ ...f, client_id: '', client_secret: '' }));
      toast.success(`PayPal config saved (mode: ${res.data.mode}). New payments will use these keys.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save PayPal config');
    } finally {
      setPpSaving(false);
    }
  };

  const addStaffMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/staff`, staffForm);
      toast.success('Staff member added!');
      setStaffForm({ name: '', email: '', role: '', revenue_share_percent: 0, paypal_email: '' });
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to add staff');
    }
  };

  const generateTaxReport = async () => {
    try {
      const res = await axios.get(`${API}/accounting/tax-report?year=${taxYear}`);
      setTaxReport(res.data);
      toast.success('Tax report generated!');
    } catch (error) {
      toast.error('Failed to generate tax report');
    }
  };

  const checkCompliance = async () => {
    try {
      const res = await axios.get(`${API}/accounting/legal-compliance-check`);
      setComplianceReport(res.data);
      toast.success('Compliance check complete!');
    } catch (error) {
      toast.error('Failed to run compliance check');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[AdminDashboard/logout] server call failed, continuing:', e?.message);
      }
    }
    navigate('/admin');
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'transactions', label: 'Transactions', icon: DollarSign },
    { id: 'staff', label: 'Staff', icon: Users },
    { id: 'tax', label: 'Tax & Compliance', icon: FileText },
    { id: 'audit', label: 'Audit Trail', icon: ScrollText },
    { id: 'orders', label: 'Orders', icon: Search },
  ];

  return (
    <div className="relative min-h-screen">
      <div className="circuit-bg" />
      <div className="circuit-overlay" />

      <div className="content-wrapper">
        {/* Top Bar */}
        <nav className="border-b border-green-500/20 backdrop-blur-md" data-testid="admin-nav">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <Shield className="w-6 h-6 text-green-500" />
                <span className="text-lg font-bold text-green-400">ADMIN DASHBOARD</span>
              </div>
              <Button onClick={handleLogout} variant="ghost" className="text-red-400 hover:text-red-300" data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {secretStatus && (secretStatus.jwt_source !== 'env' || secretStatus.admin_password_source !== 'env') && (() => {
            const ephemeral = secretStatus.jwt_source === 'ephemeral' || secretStatus.admin_password_source === 'ephemeral';
            const box = ephemeral ? 'border-red-500/50 bg-red-500/10' : 'border-amber-500/50 bg-amber-500/10';
            const accent = ephemeral ? 'text-red-400' : 'text-amber-400';
            return (
              <div className={`mb-6 rounded-lg border p-4 ${box}`} data-testid="secret-status-banner">
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${accent}`} />
                  <div className="text-sm">
                    <p className={`font-bold ${accent}`} data-testid="secret-status-title">
                      {ephemeral ? 'Degraded: running on an EPHEMERAL secret' : 'Running on a database-provisioned secret'}
                    </p>
                    <p className="text-green-300 mt-1">
                      JWT secret: <b className="uppercase">{secretStatus.jwt_source}</b> · Admin password: <b className="uppercase">{secretStatus.admin_password_source}</b>
                    </p>
                    <p className="text-green-300/80 mt-1">
                      {ephemeral
                        ? 'MongoDB was unreachable at boot, so an in-memory secret is in use — sessions reset on every restart. Set JWT_SECRET_KEY in your deployment env editor and redeploy.'
                        : 'The app self-provisioned a strong secret in the database so it could boot without env config. For full control and stability across redeploys, set JWT_SECRET_KEY (and optionally ADMIN_PASSWORD, SECRETS_ENCRYPTION_KEY) in your deployment env editor.'}
                    </p>
                    {secretStatus.reveal_available && (
                      <p className="text-yellow-300/90 mt-1">
                        Your auto-generated admin password can be revealed once from the admin login page ("First-time setup").
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
            {tabs.map(tab => (
              <Button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${activeTab === tab.id ? 'btn-primary' : 'btn-secondary'} px-4 py-2`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4 mr-2" /> {tab.label}
              </Button>
            ))}
          </div>

          {activeTab === 'overview' && stats && (
            <OverviewTab
              stats={stats}
              donations={donations}
              revenueSeries={revenueSeries}
              revenueRange={revenueRange}
              setRevenueRange={setRevenueRange}
              paypal={{ ppConfig, ppForm, setPpForm, ppShowSecret, setPpShowSecret, ppSaving, savePaypalConfig }}
              integrations={{ intConfig, intForm, setIntForm, intShowKey, setIntShowKey, intSaving, saveIntegrations }}
              rotation={{ rotating, rotationResult, rotateAdminPassword }}
            />
          )}

          {activeTab === 'transactions' && <TransactionsTab donations={donations} />}

          {activeTab === 'staff' && (
            <StaffTab
              pendingPayments={pendingPayments}
              pendingTotalOwed={pendingTotalOwed}
              staffSummary={staffSummary}
              markingPaidId={markingPaidId}
              markStaffPaymentPaid={markStaffPaymentPaid}
              fetchPendingPayments={fetchPendingPayments}
              staffForm={staffForm}
              setStaffForm={setStaffForm}
              addStaffMember={addStaffMember}
              staff={staff}
            />
          )}

          {activeTab === 'tax' && (
            <TaxTab
              taxYear={taxYear}
              setTaxYear={setTaxYear}
              generateTaxReport={generateTaxReport}
              downloadTaxPacket={downloadTaxPacket}
              downloadingPacket={downloadingPacket}
              taxReport={taxReport}
              checkCompliance={checkCompliance}
              complianceReport={complianceReport}
            />
          )}

          {activeTab === 'audit' && (
            <AuditTab
              auditLogs={auditLogs}
              auditActions={auditActions}
              auditFilter={auditFilter}
              setAuditFilter={setAuditFilter}
              auditTotal={auditTotal}
              auditLoading={auditLoading}
              fetchAuditLogs={fetchAuditLogs}
              csvRange={csvRange}
              setCsvRange={setCsvRange}
              exportingCsv={exportingCsv}
              exportAuditCsv={exportAuditCsv}
            />
          )}

          {activeTab === 'orders' && <OrdersTab />}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
